import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Reports.css';

const Reports: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [subject, setSubject] = useState('EUDR Compliance Analysis Results');
  const [includeReport, setIncludeReport] = useState(true);
  const [includeGeoJSON, setIncludeGeoJSON] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [customerId, setCustomerId] = useState('');

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      const authenticated = apiService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        try {
          const userData = apiService.getStoredUser();
          if (userData) {
            setUserEmail(userData.email);
            setCustomerId(userData.customer_id);
          }
        } catch (error) {
          console.error('Error getting user data:', error);
        }
      } else {
        // For non-authenticated users, get customer ID from URL
        const urlCustomerId = searchParams.get('customer_id');
        if (urlCustomerId) {
          setCustomerId(urlCustomerId);
        }
      }
    };
    
    checkAuth();
  }, [searchParams]);

  const handleSendEmail = async () => {
    if (isAuthenticated) {
      // For authenticated users, send to their registered email
      if (!userEmail) {
        setMessage({ type: 'error', text: 'User email not found' });
        return;
      }
    } else {
      setMessage({ type: 'error', text: 'Email reports are only available for authenticated users' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const result = await apiService.sendEmailAuthenticated({
        subject,
        include_report: includeReport,
        include_geojson: includeGeoJSON
      });
      
      const message = result?.message || `Email sent successfully to ${userEmail}!`;
      setMessage({ type: 'success', text: message });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to send email'
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    setMessage(null);

    try {
      if (isAuthenticated) {
        const blob = await apiService.downloadReportAuthenticated();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `EUDR_Report_${customerId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        setMessage({ type: 'success', text: 'Report downloaded successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Download feature is only available for authenticated users' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to download report'
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="reports-page">
      <h1>Generate Reports</h1>
      <p className="page-description">
        {isAuthenticated 
          ? `Send EUDR compliance analysis reports to your registered email (${userEmail})`
          : 'Please log in to generate and send reports'
        }
      </p>

      <div className="reports-container">
        {/* Authenticated User Info */}
        {isAuthenticated ? (
          <div className="user-info-section">
            <h3>User Information</h3>
            <div className="user-info">
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{userEmail}</span>
              </div>
              <div className="info-item">
                <span className="label">Customer ID:</span>
                <span className="value">{customerId}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="auth-required">
            <h3>Authentication Required</h3>
            <p>Please log in to access report generation features.</p>
            <button 
              onClick={() => window.location.href = '/login'}
              className="btn primary"
            >
              Go to Login
            </button>
          </div>
        )}

        {/* Report Options - Only show for authenticated users */}
        {isAuthenticated && (
          <div className="report-actions-section">
            <h3>Report Options</h3>
            
            <div className="form-group">
              <label htmlFor="subject">Email Subject:</label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </div>

            <div className="form-group">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeReport}
                    onChange={(e) => setIncludeReport(e.target.checked)}
                  />
                  Include PDF Report
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeGeoJSON}
                    onChange={(e) => setIncludeGeoJSON(e.target.checked)}
                  />
                  Include GeoJSON Files
                </label>
              </div>
            </div>

            <div className="action-buttons">
              <button 
                onClick={handleSendEmail} 
                disabled={sending || !userEmail}
                className="btn primary"
              >
                {sending ? 'Sending...' : `Send Email Report to ${userEmail}`}
              </button>
              
              <button 
                onClick={handleDownloadReport}
                disabled={downloading}
                className="btn secondary"
              >
                {downloading ? 'Preparing...' : 'Download Report'}
              </button>
            </div>

            {message && (
              <div className={`message ${message.type}`}>
                {message.type === 'success' ? '✓' : '✗'} {message.text}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Reports;